import type { AnalyticsResolutionEnum } from '@src/utils/getAnalyticsResolution';
import { format, setAlpha, type Security } from '@talos/kyoko';
import {
  CrosshairMode,
  LineStyle,
  type BarPrice,
  type IChartApi,
  type ISeriesApi,
  type LogicalRange,
  type SeriesType,
  type UTCTimestamp,
} from 'lightweight-charts';
import type { DefaultTheme } from 'styled-components';

export const TrajectoryZoomMode = {
  ZoomFit: 'ZoomFit',
  ZoomClip: 'ZoomClip',
  None: 'None',
} as const;
export type TrajectoryZoomMode = (typeof TrajectoryZoomMode)[keyof typeof TrajectoryZoomMode];

export interface OrderTrajectoryZoom {
  visibleRange: LogicalRange | null;
  updateLegIndex: number | null;
  mode: TrajectoryZoomMode;
}

export interface TrajectorySeries {
  upperQtyBound?: ISeriesApi<'Line'>;
  lowerQtyBound?: ISeriesApi<'Line'>;
  execQty?: ISeriesApi<'Line'>;
  spacing?: ISeriesApi<'Line'>;
  predLowerQtyBound?: ISeriesApi<'Line'>;
  predUpperQtyBound?: ISeriesApi<'Line'>;
  predExecQty?: ISeriesApi<'Line'>;
}

export const defaultTrajectoryZoom: OrderTrajectoryZoom = {
  visibleRange: null,
  updateLegIndex: null,
  mode: TrajectoryZoomMode.ZoomFit,
};

export const colors = {
  upperQtyBound: '#F0A211',
  lowerQtyBound: '#00BCD6',
  execQty: '#22CAAD',
  predUpperQtyBound: '#9258D7',
  predLowerQtyBound: '#9258D7',
  predExecQty: '#45A3F9',
};

export function createSeries(chart: IChartApi): TrajectorySeries {
  const upperQtyBoundLine = chart.addLineSeries({
    color: colors.upperQtyBound,
    priceLineVisible: false,
    crosshairMarkerVisible: false,
    lineWidth: 1,
    lineStyle: LineStyle.Dashed,
    visible: true,
  });
  const lowerQtyBoundLine = chart.addLineSeries({
    color: colors.lowerQtyBound,
    priceLineVisible: false,
    lineWidth: 1,
    lineStyle: LineStyle.Dashed,
    crosshairMarkerVisible: false,
    visible: true,
  });
  const execQtyLine = chart.addLineSeries({
    color: setAlpha(0.8, colors.execQty),
    priceLineVisible: false,
    crosshairMarkerVisible: false,
    lineWidth: 2,
    visible: true,
  });
  const spacingLine = chart.addLineSeries({
    priceLineVisible: false,
    crosshairMarkerVisible: false,
    visible: false,
  });
  const predUpperQtyBoundLine = chart.addLineSeries({
    color: setAlpha(0.5, colors.upperQtyBound),
    priceLineVisible: false,
    crosshairMarkerVisible: false,
    lastValueVisible: false,
    lineWidth: 1,
    visible: true,
  });
  const predLowerQtyBoundLine = chart.addLineSeries({
    color: setAlpha(0.5, colors.lowerQtyBound),
    priceLineVisible: false,
    crosshairMarkerVisible: false,
    lastValueVisible: false,
    lineWidth: 1,
    visible: true,
  });
  const predExecQtyLine = chart.addLineSeries({
    color: setAlpha(0.5, colors.predExecQty),
    priceLineVisible: false,
    crosshairMarkerVisible: false,
    lastValueVisible: false,
    lineWidth: 1,
    visible: true,
  });

  return {
    upperQtyBound: upperQtyBoundLine,
    lowerQtyBound: lowerQtyBoundLine,
    execQty: execQtyLine,
    spacing: spacingLine,
    predUpperQtyBound: predUpperQtyBoundLine,
    predLowerQtyBound: predLowerQtyBoundLine,
    predExecQty: predExecQtyLine,
  };
}

export function setChartOptions(
  chart: IChartApi,
  theme: DefaultTheme,
  security: Security,
  height: number,
  width?: number
) {
  chart.applyOptions({
    height,
    width,
    localization: {
      priceFormatter: (price: BarPrice) => format(price, { spec: security.DefaultPriceIncrement, round: true }),
    },
    timeScale: {
      timeVisible: true,
      secondsVisible: true,
      borderColor: theme.borderColorChartAxis,
      rightOffset: theme.spacing4,
      shiftVisibleRangeOnNewBar: false,
      // below is not ideal, but currently we cannot request a more coarse resolution from the trajectory-service
      // so for super long orders, we're plotting a lot of points
      minBarSpacing: 0.005,
    },
    rightPriceScale: {
      borderColor: theme.borderColorChartAxis,
    },
    layout: {
      background: {
        color: 'transparent',
      },
      textColor: theme.colors.gray['080'],
      fontFamily: 'Roboto',
      fontSize: 10,
    },
    crosshair: {
      vertLine: {
        color: theme.colors.gray['040'],
      },
      horzLine: {
        visible: false,
        labelBackgroundColor: 'transparent',
      },
      mode: CrosshairMode.Normal,
    },
    grid: {
      vertLines: {
        visible: true,
        color: theme.borderColorChartAxis,
      },
      horzLines: {
        visible: true,
        color: theme.borderColorChartAxis,
      },
    },
  });
}

export function getChartRange(chartSeries: TrajectorySeries): { from: UTCTimestamp; to: UTCTimestamp } {
  const firstPoint = firstSeriesPoint(chartSeries.execQty) ?? firstSeriesPoint(chartSeries.predExecQty);
  const lastPoint = lastSeriesPoint(chartSeries.execQty) ?? lastSeriesPoint(chartSeries.predExecQty);

  if (!firstPoint || !lastPoint || typeof firstPoint.time !== 'number' || typeof lastPoint.time !== 'number') {
    return { from: 0 as UTCTimestamp, to: 0 as UTCTimestamp };
  }

  return { from: firstPoint.time, to: lastPoint.time };
}

const SECONDS: Record<AnalyticsResolutionEnum, number> = {
  '1S': 1,
  '10S': 10,
  '1M': 60,
  '1H': 3600,
  '1D': 86400,
} as const;

export function ensureSeriesSpacing(
  chartSeries: TrajectorySeries,
  requestedResolution: AnalyticsResolutionEnum | undefined
) {
  if (!requestedResolution || firstSeriesPoint(chartSeries.spacing) != null) {
    return;
  }

  const firstExecTime = (
    firstSeriesPoint(chartSeries.lowerQtyBound) ||
    firstSeriesPoint(chartSeries.upperQtyBound) ||
    firstSeriesPoint(chartSeries.execQty)
  )?.time;

  const lastPredTime = (
    lastSeriesPoint(chartSeries.predLowerQtyBound) ||
    lastSeriesPoint(chartSeries.predUpperQtyBound) ||
    lastSeriesPoint(chartSeries.predExecQty)
  )?.time;

  if (firstExecTime == null || lastPredTime == null) {
    return;
  }

  const interval = SECONDS[requestedResolution];

  const data: { time: UTCTimestamp; value: number }[] = [];
  for (let time = firstExecTime as number; time < (lastPredTime as number); time += interval) {
    data.push({ time: time as UTCTimestamp, value: 5 });
  }

  chartSeries.spacing?.setData(data);
}

export function firstSeriesPoint<T extends SeriesType>(series: ISeriesApi<T> | undefined) {
  if (!series) {
    return null;
  }
  return series.dataByIndex(0, 1);
}

export function lastSeriesPoint<T extends SeriesType>(series: ISeriesApi<T> | undefined) {
  if (!series) {
    return null;
  }
  return series.dataByIndex(Number.MAX_SAFE_INTEGER, -1);
}
